/*
 * RSDESK — Design Tokens
 * =============================================================
 * Fonte unica de verdade para cor, tipografia, espacamento, raio e
 * sombra do sistema inteiro.
 *
 * REGRA: nenhum componente deve declarar cor em hexadecimal, tamanho de
 * fonte em pixel ou sombra propria. Tudo sai daqui, via `theme`.
 * =============================================================
 */

/* -------------------------------------------------------------
 * 1. PALETA — Azul marinho (quase preto)
 * A rampa vai do quase-preto (950) ao quase-branco (50). Os tons
 * 600/700 sao a cor de marca; 850/900/950 formam o "chrome" do
 * sistema (sidebar, barras, superficies do modo escuro).
 * ----------------------------------------------------------- */
export const navy = {
  50: "#EEF3FA",
  100: "#D7E3F3",
  200: "#AFC5E3",
  300: "#7C9BC9",
  400: "#4A6FA8",
  500: "#2A4C82",
  600: "#193864", // marca — acoes primarias
  700: "#122A4C", // marca escura — hover/pressionado
  800: "#0D1E38",
  850: "#0A1728",
  900: "#07101D", // sidebar
  950: "#040A12"  // sidebar topo / mais escuro do sistema
};

/* Acento: usado com parcimonia — item ativo, foco, links sobre navy. */
export const accent = {
  400: "#5B9BFF",
  500: "#3B82F6",
  600: "#2563EB"
};

/* Neutros para texto, bordas e superficies claras. */
export const neutral = {
  0: "#FFFFFF",
  50: "#F7F9FC",
  100: "#EEF2F7",
  200: "#E1E8F0",
  300: "#CBD5E1",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A"
};

/* Estados semanticos — um tom para claro, um para escuro. */
export const semantic = {
  success: { light: "#15803D", dark: "#4ADE80", soft: "#DCFCE7", softDark: "rgba(74, 222, 128, 0.14)" },
  warning: { light: "#B45309", dark: "#FBBF24", soft: "#FEF3C7", softDark: "rgba(251, 191, 36, 0.14)" },
  danger:  { light: "#B91C1C", dark: "#F87171", soft: "#FEE2E2", softDark: "rgba(248, 113, 113, 0.14)" },
  info:    { light: "#0E5FA8", dark: "#7CB4F5", soft: "#E0EDFB", softDark: "rgba(124, 180, 245, 0.14)" }
};

/* -------------------------------------------------------------
 * 2. TIPOGRAFIA
 * Escala modular de razao ~1.2 ancorada em 16px.
 * Substitui os 17 tamanhos avulsos que existiam espalhados no codigo.
 *
 * Poppins Black (900)   -> titulos e subtitulos, CAIXA ALTA
 * Poppins Medium (500)  -> subtitulos e titulos curtos, CAIXA ALTA
 * Poppins Regular (400) -> corpo de texto, caixa normal
 *
 * Os tres pesos vem do Google Fonts, declarados em index.html. Sobre a
 * familia da marca e por que ela nao esta aqui, ver o cabecalho de
 * styles/fonts.css.
 * ----------------------------------------------------------- */
export const fontFamily =
  '"Poppins", "Inter", "Helvetica Neue", Arial, sans-serif';

export const fontWeight = {
  regular: 400,
  medium: 500,
  black: 900
};

/* Passos da escala, em rem (base 16px). */
export const fontSize = {
  xs: "0.6875rem", // 11px — overline, badges
  sm: "0.75rem",   // 12px — legenda, cabecalho de tabela
  md: "0.8125rem", // 13px — texto denso (tabelas, listas)
  base: "0.875rem",// 14px — corpo padrao da interface
  lg: "1rem",      // 16px — corpo longo, leitura
  xl: "1.125rem",  // 18px — subtitulo
  "2xl": "1.375rem", // 22px — titulo de secao
  "3xl": "1.75rem",  // 28px — titulo de pagina
  "4xl": "2.25rem"   // 36px — display / numeros de destaque
};

export const lineHeight = {
  tight: 1.15,  // titulos grandes
  snug: 1.3,    // titulos pequenos
  normal: 1.5,  // interface
  relaxed: 1.65 // leitura longa
};

/* Caixa alta precisa de mais respiro entre letras; corpo nao. */
export const letterSpacing = {
  caps: "0.06em",
  capsTight: "0.03em",
  normal: "0em",
  wide: "0.01em"
};

/* -------------------------------------------------------------
 * 3. ESPACAMENTO, RAIO E ELEVACAO
 * Grade de 8px. Raio unificado (antes havia 10 valores diferentes:
 * 0, 4, 6, 8, 9, 10, 12, 16, 20, 999).
 * ----------------------------------------------------------- */
/*
 * Espacamento — em REM, nao em pixel.
 * =============================================================
 * Era `8` (numero puro), e o Material-UI converte numero em PIXEL.
 * Isso travava as ~180 chamadas de theme.spacing() do sistema inteiro
 * em px fixo: elas ignoravam qualquer ajuste de escala.
 *
 * Como funcao devolvendo rem, todas passam a seguir o font-size da
 * raiz — que e o eixo unico definido em styles/fonts.css.
 * 0.5rem = 8px quando a raiz esta em 16px, entao o visual em monitor
 * grande fica identico ao de antes.
 * =============================================================
 */
export const spacingUnit = fator => `${fator * 0.5}rem`;

/*
 * Proporcao dos controles: altura ÷ tamanho da fonte.
 * =============================================================
 * As alturas eram numeros soltos e produziam razoes incoerentes:
 * botao 40/13 = 3,08 · botao grande 48/14 = 3,43 · aba 46/13 = 3,54 —
 * todas acima do mercado (Material 2,57 · Bootstrap 2,38 · Ant 2,29).
 * Caixa folgada com texto pequeno e o que faz um controle parecer
 * grande e vazio.
 *
 * A altura agora DERIVA da fonte por uma razao unica, e em rem — logo
 * acompanha tambem a escala da raiz. Para mudar a densidade dos
 * controles, mexe-se em CONTROL_RATIO e tudo segue junto.
 * =============================================================
 */
/*
 * Conversor unico de px de referencia para rem.
 * =============================================================
 * Regra de uso, para nao voltar ao caos anterior:
 *   theme.spacing(n)  -> respiro na grade de 8
 *   rem(px)           -> geometria fora da grade
 *   numero cru        -> SO para hairline de 1px, pill (999),
 *                        zIndex, flex e opacity
 * =============================================================
 */
export const REM_BASE = 16;

export const rem = px => `${+(px / REM_BASE).toFixed(4)}rem`;

export const CONTROL_RATIO = 2.45;

/* Altura de controle em rem, a partir do tamanho da fonte em rem. */
const alturaControle = fonteRem => `${(fonteRem * CONTROL_RATIO).toFixed(3)}rem`;

export const controlHeight = {
  sm: alturaControle(0.75),   // fonte 12px
  md: alturaControle(0.875),  // fonte 14px
  lg: alturaControle(1)       // fonte 16px
};

export const radius = {
  sm: 6,    // chips pequenos, badges
  md: 10,   // botoes, inputs, cards internos
  lg: 14,   // cards e paineis
  xl: 20,   // dialogos, superficies de destaque
  pill: 999 // chips de status
};

export const elevation = {
  light: {
    xs: "0 1px 2px rgba(7, 16, 29, 0.06)",
    sm: "0 2px 8px rgba(7, 16, 29, 0.07)",
    md: "0 8px 24px rgba(7, 16, 29, 0.08)",
    lg: "0 18px 48px rgba(7, 16, 29, 0.12)"
  },
  dark: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.4)",
    sm: "0 2px 8px rgba(0, 0, 0, 0.45)",
    md: "0 8px 24px rgba(0, 0, 0, 0.5)",
    lg: "0 18px 48px rgba(0, 0, 0, 0.6)"
  }
};

/* Duracoes de transicao — antes variavam entre 120ms e 180ms sem criterio. */
export const motion = {
  fast: "120ms cubic-bezier(0.4, 0, 0.2, 1)",
  base: "180ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "260ms cubic-bezier(0.4, 0, 0.2, 1)"
};

/* -------------------------------------------------------------
 * 4. TOKENS SEMANTICOS POR TEMA
 * Os componentes consomem estes nomes (surface, border, sidebar...),
 * nunca a rampa crua acima. Trocar a marca = mexer so aqui.
 * ----------------------------------------------------------- */
export const lightTokens = {
  surface: neutral[0],
  surfaceSoft: neutral[50],
  surfaceSunken: neutral[100],
  appBackground: "#F2F5FA",

  sidebar: navy[900],
  sidebarStrong: navy[950],
  sidebarSoft: navy[800],
  sidebarText: "rgba(255, 255, 255, 0.72)",
  sidebarTextActive: neutral[0],
  sidebarActiveBg: "rgba(91, 155, 255, 0.16)",

  header: neutral[0],
  border: neutral[200],
  borderStrong: neutral[300],

  textPrimary: navy[850],
  textSecondary: neutral[500],
  textDisabled: neutral[400],
  textInverse: neutral[0],

  primary: navy[600],
  primaryHover: navy[700],
  primarySoft: navy[50],
  primaryContrast: neutral[0],

  accent: accent[600],
  accentSoft: "#E3EDFD",

  success: semantic.success.light,
  successSoft: semantic.success.soft,
  warning: semantic.warning.light,
  warningSoft: semantic.warning.soft,
  danger: semantic.danger.light,
  dangerSoft: semantic.danger.soft,
  info: semantic.info.light,
  infoSoft: semantic.info.soft,

  shadow: elevation.light
};

export const darkTokens = {
  surface: navy[850],
  surfaceSoft: navy[800],
  surfaceSunken: navy[900],
  appBackground: navy[900],

  sidebar: navy[950],
  sidebarStrong: "#02060B",
  sidebarSoft: navy[850],
  sidebarText: "rgba(255, 255, 255, 0.66)",
  sidebarTextActive: neutral[0],
  sidebarActiveBg: "rgba(91, 155, 255, 0.18)",

  header: navy[900],
  border: "rgba(255, 255, 255, 0.09)",
  borderStrong: "rgba(255, 255, 255, 0.16)",

  textPrimary: "#E6EDF7",
  textSecondary: "#94A6BF",
  textDisabled: "#5A6E8A",
  textInverse: navy[900],

  primary: accent[400],
  primaryHover: accent[500],
  primarySoft: "rgba(91, 155, 255, 0.14)",
  primaryContrast: navy[950],

  accent: accent[400],
  accentSoft: "rgba(91, 155, 255, 0.14)",

  success: semantic.success.dark,
  successSoft: semantic.success.softDark,
  warning: semantic.warning.dark,
  warningSoft: semantic.warning.softDark,
  danger: semantic.danger.dark,
  dangerSoft: semantic.danger.softDark,
  info: semantic.info.dark,
  infoSoft: semantic.info.softDark,

  shadow: elevation.dark
};

/* Cores de status de atendimento — derivadas dos tokens acima. */
export const statusPalette = tokens => ({
  waiting: { bg: tokens.warningSoft, color: tokens.warning },
  open: { bg: tokens.successSoft, color: tokens.success },
  closed: { bg: tokens.surfaceSunken, color: tokens.textSecondary },
  error: { bg: tokens.dangerSoft, color: tokens.danger }
});
